import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input, output } from '@angular/core';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../bodygraph/hd-gate-info.service';
import { HD_GLOBAL_CYCLES_ROWS, HdGlobalCyclesBlockColor } from './hd-global-cycles-table.data';
import {
  HdLocksKeysPeriodId,
  HdLocksKeysStarGroup,
  getHdLocksKeysPeriodMeta,
} from './hd-locks-keys-metadata';
import { hdLocksKeysYearRangeLabel } from './hd-locks-keys-period-text';

/** Ontwerpmaten van de vlecht, op de vaste breedte van de Global Cycles-tabel (960px). */
export const HD_GLOBAL_CYCLES_BRAID_WIDTH = 960;
export const HD_GLOBAL_CYCLES_BRAID_HEIGHT = 112;
const COLUMN_START_X = 92;
const COLUMN_WIDTH = 17.6;
const LANE_Y: Readonly<Record<HdLocksKeysStarGroup, number>> = { sphinx: 36, vessel: 68 };
const BAND_TOP = 20;
const BAND_HEIGHT = 64;

interface BraidColumnVm {
  readonly id: string;
  readonly periodId: HdLocksKeysPeriodId;
  readonly x: number;
  readonly isLock: boolean;
  /** Poort van het lentepunt: op een lock-rij de lock waarop het landt. */
  readonly equinoxGate: number;
  readonly isCurrent: boolean;
  readonly tooltip: string;
}

interface BraidBlockVm {
  readonly id: string;
  readonly color: HdGlobalCyclesBlockColor;
  readonly x: number;
  readonly width: number;
}

/**
 * Vlecht van de keys-ster door de 49 tijdperken van de Global Cycles-tabel:
 * twee banen (Sphinx en Vessel) en twee strengen, de twee vierkanten van de
 * keys-ster. De felle streng is het vierkant van het lentepunt (kolom 1 links
 * in de tabel); bij elke lock-rij kruisen beide strengen naar het andere
 * slotkruis. Zo is de kantwissel van de tabel in één blik zichtbaar. Zelfde
 * blokkleuren en vaste breedte als de tabel, zodat de kolommen eronder passen.
 */
@Component({
  selector: 'hd-global-cycles-braid',
  templateUrl: './hd-global-cycles-braid.component.html',
  styleUrl: './hd-global-cycles-braid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdGlobalCyclesBraidComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Kolom van deze app-periode krijgt de dusk-blauwe selectierand; null = geen. */
  readonly highlightPeriodId = input<HdLocksKeysPeriodId | null>(null);

  /** Kolommen klikbaar maken (zwevend tabelpaneel). */
  readonly selectable = input(false, { transform: booleanAttribute });

  /** Klik op een kolom: elke kolom is een Locks & Keys-periode. */
  readonly periodSelected = output<HdLocksKeysPeriodId>();

  protected readonly width = HD_GLOBAL_CYCLES_BRAID_WIDTH;
  protected readonly height = HD_GLOBAL_CYCLES_BRAID_HEIGHT;
  protected readonly laneY = LANE_Y;
  protected readonly bandTop = BAND_TOP;
  protected readonly bandHeight = BAND_HEIGHT;
  protected readonly columnWidth = COLUMN_WIDTH;

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.gateInfo.locksKeysTextIn(`globalCycles.${key}`, this.lang(), params);
  }

  protected readonly columns = computed<readonly BraidColumnVm[]>(() => {
    const lang = this.lang();
    return HD_GLOBAL_CYCLES_ROWS.map((row, index) => {
      const year = hdLocksKeysYearRangeLabel(this.gateInfo, lang, getHdLocksKeysPeriodMeta(row.periodId));
      const tooltip = row.kind === 'lock'
        ? this.t('braidLockTooltip', { year, gate: row.leftGates[0] })
        : this.t('braidKeyTooltip', {
            year,
            gate: row.leftGates[0],
            group: this.t(row.leftGroup === 'sphinx' ? 'groupSphinx' : 'groupVessel'),
            cross: `${row.leftCross} ${row.leftVariant}`,
          });
      return {
        id: row.id,
        periodId: row.periodId,
        x: columnX(index),
        isLock: row.kind === 'lock',
        equinoxGate: row.leftGates[0],
        isCurrent: row.isCurrent,
        tooltip,
      };
    });
  });

  /** Achtergrondband per blok in de blokkleur van de tabel. */
  protected readonly blocks: readonly BraidBlockVm[] = buildBlocks();

  /** Streng van het vierkant van het lentepunt: de baan van het kruis links in de tabel. */
  protected readonly equinoxStrand = HD_GLOBAL_CYCLES_ROWS
    .map((row, index) => `${columnX(index)},${LANE_Y[row.leftGroup]}`)
    .join(' ');

  /** Streng van het andere vierkant: steeds de andere baan. */
  protected readonly counterStrand = HD_GLOBAL_CYCLES_ROWS
    .map((row, index) => `${columnX(index)},${LANE_Y[row.rightGroup]}`)
    .join(' ');

  protected readonly highlightColumn = computed(() => {
    const id = this.highlightPeriodId();
    return id == null ? null : this.columns().find(column => column.periodId === id) ?? null;
  });

  protected readonly currentColumn = computed(() => this.columns().find(column => column.isCurrent) ?? null);

  protected select(periodId: HdLocksKeysPeriodId): void {
    if (this.selectable()) {
      this.periodSelected.emit(periodId);
    }
  }
}

function columnX(index: number): number {
  return Math.round((COLUMN_START_X + index * COLUMN_WIDTH) * 10) / 10;
}

function buildBlocks(): readonly BraidBlockVm[] {
  const blocks: BraidBlockVm[] = [];
  HD_GLOBAL_CYCLES_ROWS.forEach((row, index) => {
    const last = blocks[blocks.length - 1];
    if (last != null && last.color === row.blockColor && row.kind === 'key') {
      blocks[blocks.length - 1] = { ...last, width: last.width + COLUMN_WIDTH };
      return;
    }
    blocks.push({ id: row.id, color: row.blockColor, x: columnX(index) - COLUMN_WIDTH / 2, width: COLUMN_WIDTH });
  });
  return blocks;
}
